/**
 * API service for Neubird Messaging Service
 * Handles all backend communication
 */
import axios, { AxiosInstance } from 'axios';
import { User, Message, BirdType, LoginForm, RegisterForm, Location } from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  private endpoints: string[];
  private currentEndpointIndex: number;

  constructor() {
    // Multi-region endpoints for load distribution
    // Uses environment variables from vite.config.ts
    this.endpoints = [
      import.meta.env.VITE_API_URL || 'https://nb-ds-api.htm.neubird.ai',   // us-west-2
      import.meta.env.VITE_API_URL_EAST || 'https://nb-ds-api2.htm.neubird.ai'   // us-east-1
    ];
    
    // Randomly select initial endpoint on page load
    this.currentEndpointIndex = Math.floor(Math.random() * this.endpoints.length);
    this.baseURL = this.endpoints[this.currentEndpointIndex];
    
    console.log(`API Service initialized with ${this.currentEndpointIndex === 0 ? 'WEST' : 'EAST'} endpoint: ${this.baseURL}`);
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('neubird_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and failover
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Don't auto-redirect if this is a login request - let the login handler deal with it
          const isLoginRequest = error.config?.url?.includes('/auth/login');
          
          if (!isLoginRequest) {
            // Token expired or invalid on non-login requests
            localStorage.removeItem('neubird_token');
            localStorage.removeItem('neubird_user');
            // Redirect to login with a message about session expiry
            window.location.href = '/login?expired=true';
          }
        }
        
        // Automatic failover on network errors or 5xx errors
        if (!error.response || error.response.status >= 500) {
          const isRetry = error.config?._retry;
          
          if (!isRetry) {
            // Try the other endpoint
            const otherEndpointIndex = (this.currentEndpointIndex + 1) % this.endpoints.length;
            const otherEndpoint = this.endpoints[otherEndpointIndex];
            
            console.warn(`Request failed on ${this.baseURL}, retrying on ${otherEndpoint}`);
            
            error.config._retry = true;
            error.config.baseURL = otherEndpoint;
            
            return this.api.request(error.config);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }
  
  // Switch to alternate endpoint (for manual region switching)
  switchRegion() {
    this.currentEndpointIndex = (this.currentEndpointIndex + 1) % this.endpoints.length;
    this.baseURL = this.endpoints[this.currentEndpointIndex];
    this.api.defaults.baseURL = this.baseURL;
    console.log(`Switched to ${this.currentEndpointIndex === 0 ? 'WEST' : 'EAST'} endpoint: ${this.baseURL}`);
  }
  
  // Get current region
  getCurrentRegion(): string {
    return this.currentEndpointIndex === 0 ? 'us-west-2' : 'us-east-1';
  }

  // Authentication methods
  async login(credentials: LoginForm): Promise<{ user: User; token: string }> {
    try {
      const response = await this.api.post('/auth/login', credentials);
      const { access_token, user } = response.data;
      
      // Store token
      localStorage.setItem('neubird_token', access_token);
      localStorage.setItem('neubird_user', JSON.stringify(user));
      
      return { user, token: access_token };
    } catch (error: any) {
      // For login, we want to preserve the original error details including status code
      const errorMessage = error.response?.data?.detail || 'Login failed';
      const newError = new Error(errorMessage);
      (newError as any).response = error.response; // Preserve response for status code checking
      throw newError;
    }
  }

  async register(userData: RegisterForm): Promise<{ user: User; token: string }> {
    try {
      const response = await this.api.post('/auth/register', {
        name: userData.name,
        email: userData.email,
        password: userData.password
      });
      const { access_token, user } = response.data;
      
      // Store token
      localStorage.setItem('neubird_token', access_token);
      localStorage.setItem('neubird_user', JSON.stringify(user));
      
      return { user, token: access_token };
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  }

  async verifyToken(): Promise<User> {
    try {
      const response = await this.api.get('/auth/verify');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Token verification failed');
    }
  }

  async createServiceAccountToken(email: string): Promise<{
    message: string;
    service_account: {
      email: string;
      user_id: string;
      name: string;
      created_at: string;
    };
    token: {
      access_token: string;
      token_type: string;
      expires_at: string;
      validity_years: number;
    };
    usage: string;
  }> {
    try {
      const response = await this.api.post(`/auth/service-account?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create service account token');
    }
  }

  // Bird types
  async getBirdTypes(): Promise<BirdType[]> {
    try {
      const response = await this.api.get('/bird-types');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch bird types');
    }
  }

  // Message methods
  async getMessages(): Promise<Message[]> {
    try {
      const response = await this.api.get('/messages/');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch messages');
    }
  }

  async getMessageById(messageId: string): Promise<Message> {
    try {
      const response = await this.api.get(`/messages/${messageId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch message');
    }
  }

  async sendMessage(messageData: {
    content: string;
    origin: Location;
    destination: Location;
    birdTypeId: string;
  }): Promise<Message> {
    try {
      const response = await this.api.post('/messages/', {
        content: messageData.content,
        origin: {
          latitude: messageData.origin.latitude,
          longitude: messageData.origin.longitude,
          address: messageData.origin.address,
          city: messageData.origin.city,
          country: messageData.origin.country
        },
        destination: {
          latitude: messageData.destination.latitude,
          longitude: messageData.destination.longitude,
          address: messageData.destination.address,
          city: messageData.destination.city,
          country: messageData.destination.country
        },
        bird_type_id: messageData.birdTypeId
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to send message');
    }
  }

  // User methods
  async getUserProfile(userId: string): Promise<User> {
    try {
      const response = await this.api.get(`/users/${userId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch user profile');
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    try {
      const response = await this.api.get('/health');
      return response.data;
    } catch (error: any) {
      throw new Error('API health check failed');
    }
  }

  // Clear all messages (testing/admin only)
  async clearAllMessages(): Promise<{
    status: string;
    message: string;
    messages_deleted: number;
    tracking_events_deleted: number;
    users_reset: number;
  }> {
    try {
      const response = await this.api.delete('/messages/clear-all');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to clear all messages');
    }
  }

  // SV Buttercup location from Windy API
  async getButtercupLocation(): Promise<Location | null> {
    try {
      const windyApiUrl = 'https://stations.windy.com/pws/station/eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjaSI6ODAyNTA4NiwiaWF0IjoxNzQ3MzU1NzcxfQ.NSt8rZfHijvQahRJbB2dcnWZve6s_zfaypNqOWCGwqo/0';
      
      // Use fetch directly for external API to avoid axios interceptors
      const response = await fetch(windyApiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Windy API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.header && data.header.lat && data.header.lon) {
        return {
          latitude: data.header.lat,
          longitude: data.header.lon,
          address: `${data.header.name || 'SV Buttercup'}, Redwood City, CA, USA`,
          city: 'Redwood City',
          country: 'USA'
        };
      }
      
      return null;
    } catch (error: any) {
      console.warn('Failed to fetch SV Buttercup location from Windy API:', error);
      // Return fallback location based on the provided data
      return {
        latitude: 37.512897,
        longitude: -122.19443466666667,
        address: 'SV Buttercup, Redwood City, CA, USA (Last Known)',
        city: 'Redwood City',
        country: 'USA'
      };
    }
  }

  // Utility methods
  setAuthToken(token: string) {
    localStorage.setItem('neubird_token', token);
  }

  getAuthToken(): string | null {
    return localStorage.getItem('neubird_token');
  }

  clearAuth() {
    localStorage.removeItem('neubird_token');
    localStorage.removeItem('neubird_user');
  }

  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;
    
    try {
      // Basic token validation (you might want to add expiry check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      return payload.exp > now;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
